import { Capacitor } from '@capacitor/core';
import { safePushRoute } from '@/lib/pushRoutes';

/**
 * Navegação ao TOCAR numa notificação push.
 *
 * Até aqui o app não tinha nenhum listener de push: o backend mandava
 * `data.type` desde junho e ninguém lia, então tocar na notificação só abria o
 * app na tela em que ele estava. Este módulo fecha essa ponta lendo
 * `data.route` (path interno, validado contra a whitelist de `pushRoutes`).
 *
 * **Cold start funciona nas duas plataformas** — verificado no código nativo,
 * não suposto:
 * - Android: `BridgeActivity.load()` chama `onNewIntent(getIntent())` logo após
 *   criar a bridge, o que alcança o `handleOnNewIntent` do plugin.
 * - Os dois lados emitem com `retainUntilConsumed = true`
 *   (`FirebaseMessagingPlugin.java` / `.swift`), então o Capacitor guarda o
 *   evento e o replaya no primeiro `addListener` — não há corrida com o boot
 *   do JS.
 *
 * Deslogado, o `RequireAuth` manda pro `/login` com `state.from` e devolve ao
 * destino depois. Não precisa do padrão de "rota pendente" do referral: aquele
 * existe porque o código chega por URL antes de haver sessão; aqui a rota é
 * interna e o router já a carrega.
 *
 * ⚠️ Nunca `await` no proxy do plugin (gotcha da release 1.1.0) — await só em
 * `import()` e em chamada de método.
 */

// appUrlOpen e notificationActionPerformed podem se sobrepor num cold start;
// dedup por rota + janela curta evita navegação dupla (mesma receita do
// deepLinks.ts).
let lastHandled: { route: string; at: number } | null = null;
let registered = false;

/** Permite re-registrar depois de um `removeAllListeners` (ex.: logout). */
export function resetPushNavigation(): void {
  registered = false;
  lastHandled = null;
}

/** Registra o listener de toque. Chamar DENTRO do Router (o navigate precisa
 *  do contexto). No-op no web/PWA e em shell sem o plugin. */
export async function initPushNavigation(
  navigate: (path: string) => void
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (registered) return;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    await FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (event) => {
        // `data` chega como `{}` na tipagem do plugin — o conteúdo é o que o
        // backend mandou. `safePushRoute` valida o valor de verdade.
        const data = event?.notification?.data as
          | Record<string, unknown>
          | undefined;
        const route = safePushRoute(data?.route);
        if (!route) return;
        const now = Date.now();
        if (lastHandled?.route === route && now - lastHandled.at < 3000) {
          return;
        }
        lastHandled = { route, at: now };
        navigate(route);
      }
    );
    registered = true;
  } catch {
    // Plugin indisponível/erro — navegação por push silenciosamente inativa.
    // O push em si continua chegando; só o toque não navega.
  }
}
